import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import NotificationModal from './notificationModal';
import apiClient from '../utils/axiosConfig';
import { useClickOutside } from '../hooks/useClickOutside';
import { Spinner } from '../components/ui/States';

const PAGE_SIZE = 10;
const POLL_MS = 30000;

const formatTimeAgo = (date) => {
  const diffMs = Date.now() - new Date(date).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

const truncateText = (text = '', maxLength) =>
  text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`;

/**
 * Shared notification bell/dropdown. `source` describes one notification feed:
 *   { title, icon, enabled, listUrl(page, type), extractList(response), countUrl(), markReadUrl(id), getId(n) }
 * Endpoints and response parsing live in the per-feed wrappers so the API calls stay exactly as before.
 */
export default function NotificationDropdown({ source }) {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const dropdownRef = useRef(null);
  const observerRef = useRef(null);
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const listRequestSeq = useRef(0);
  const Icon = source.icon;

  useClickOutside(dropdownRef, () => setIsOpen(false), isOpen && !selectedNotification);

  const fetchNotifications = useCallback(async (pageNum, tab) => {
    const seq = ++listRequestSeq.current;
    try {
      setLoading(true);
      setError(null);
      const type = tab === 'unread' ? 'unread' : 'all';
      const response = await apiClient.get(sourceRef.current.listUrl(pageNum, type));
      if (seq !== listRequestSeq.current) return;
      const list = sourceRef.current.extractList(response);
      if (list) {
        setNotifications((prev) => (pageNum === 1 ? list : [...prev, ...list]));
        setHasMore(list.length === PAGE_SIZE);
      }
    } catch (err) {
      if (seq !== listRequestSeq.current) return;
      setError('Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      if (seq === listRequestSeq.current) setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiClient.get(sourceRef.current.countUrl());
      const data = response.data.data;
      // 0 is a valid count - it must clear the badge
      if (data !== undefined && data !== null) {
        setUnreadCount(data);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  // Poll the unread count; paused while the browser tab is hidden, refreshed when it returns.
  // Re-subscribes when the feed becomes available (e.g. userId loads after mount).
  useEffect(() => {
    if (!source.enabled) return undefined;

    let timer = null;
    const start = () => {
      clearInterval(timer);
      timer = setInterval(fetchUnreadCount, POLL_MS);
    };
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(timer);
      } else {
        fetchUnreadCount();
        start();
      }
    };

    fetchUnreadCount();
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [source.enabled, source.countUrl(), fetchUnreadCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setHasMore(true);
      fetchNotifications(1, activeTab);
    }
  }, [isOpen, activeTab, fetchNotifications]);

  useEffect(() => {
    if (page > 1) {
      fetchNotifications(page, activeTab);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastNotificationRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const markAsRead = async (id) => {
    try {
      const response = await apiClient.post(sourceRef.current.markReadUrl(id));
      if (response.status === 200) {
        setNotifications((prev) =>
          prev.map((n) => (sourceRef.current.getId(n) === id ? { ...n, isRead: true, readAt: new Date() } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    if (!notification.isRead) {
      markAsRead(source.getId(notification)); // don't block opening on the request
    }
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setNotifications([]);
  };

  return (
    <div className="sm:relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="p-2 hover:bg-gray-100 rounded-full relative"
        aria-label={`${source.title}${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title={source.title}
      >
        <Icon className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 bg-red-500 rounded-full flex items-center justify-center text-white text-[11px] font-semibold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label={source.title}
          className="fixed sm:absolute inset-x-2 top-16 sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 max-h-[calc(100vh-5rem)] sm:max-h-[32rem] flex flex-col z-50"
        >
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-base font-semibold text-gray-800">{source.title}</h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1 -m-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex border-b border-gray-200" role="tablist">
            {['all', 'unread'].map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                className={`flex-1 py-2 text-sm font-medium capitalize ${
                  activeTab === tab ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => handleTabChange(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="overflow-y-auto flex-1">
            {error ? (
              <div className="p-6 text-center text-sm">
                <p className="text-red-500">{error}</p>
                <button
                  type="button"
                  onClick={() => fetchNotifications(1, activeTab)}
                  className="mt-2 text-blue-600 hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : notifications.length === 0 && loading ? (
              <div className="p-6 flex justify-center text-blue-600">
                <Spinner className="h-5 w-5" label="Loading..." />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                {activeTab === 'unread' ? "You're all caught up" : 'No notifications'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {notifications.map((notification, index) => (
                  <li
                    key={source.getId(notification)}
                    ref={index === notifications.length - 1 ? lastNotificationRef : null}
                  >
                    <button
                      type="button"
                      className={`w-full text-left p-4 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex gap-3 ${
                        notification.isRead ? '' : 'bg-blue-50/40'
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <span className="flex-shrink-0 w-9 h-9 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className={`block text-sm break-words ${notification.isRead ? 'font-medium text-gray-800' : 'font-semibold text-gray-900'}`}>
                          {truncateText(notification.title, 50)}
                        </span>
                        <span className="block text-sm text-gray-600 mt-0.5 break-words">
                          {truncateText(notification.content, 100)}
                        </span>
                        <span className="block text-xs text-gray-500 mt-1">{formatTimeAgo(notification.publishedDate)}</span>
                      </span>
                      {!notification.isRead && (
                        <span className="flex-shrink-0 mt-1.5 w-2 h-2 bg-blue-600 rounded-full" aria-label="Unread" />
                      )}
                    </button>
                  </li>
                ))}
                {loading && (
                  <li className="p-4 flex justify-center text-blue-600">
                    <Spinner className="h-4 w-4" label="Loading more..." />
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        notification={selectedNotification}
      />
    </div>
  );
}
